import { type Span, getClient, startSpan } from '@sentry/react'
import type { PortfolioDbSyncReason } from './dbSyncPolicy'

/** DB backup telemetry が記録する phase 名。 */
export type DbSyncTelemetryPhase =
  | 'db-backup.snapshot'
  | 'db-backup.compress'
  | 'db-backup.upload'
  | 'db-backup.complete'
  | 'db-backup.total'

/** DB backup 開始時に確定している非機密 metadata。 */
export type DbSyncTelemetryMetadata = {
  readonly triggerReason: PortfolioDbSyncReason
  readonly dirtyAgeMs: number
  readonly lastWriteAgeMs: number
  readonly userIdleAgeMs: number
  readonly visibilityState: DocumentVisibilityState
}

/** snapshot と upload 処理が利用する operation 単位の telemetry recorder。 */
export type DbSyncTelemetry = {
  readonly measurePhase: <Result>(params: {
    readonly phase: Exclude<DbSyncTelemetryPhase, 'db-backup.total'>
    readonly task: () => Promise<Result>
  }) => Promise<Result>
  readonly setRawDbSizeBytes: (params: { readonly bytes: number }) => void
  readonly setGzipSizeBytes: (params: { readonly bytes: number }) => void
}

/** DB backup 中に集計した long task の要約。 */
export type DbSyncLongTaskSummary = {
  readonly count: number
  readonly longestDurationMs: number
}

/** long task 集計を停止して結果を返す callback。 */
type StopLongTaskObserver = () => DbSyncLongTaskSummary

/** この operation が Sentry span へ送る scalar 属性。 */
type DbSyncSpanAttributes = Readonly<
  Record<string, string | number | boolean | undefined>
>

/** operation ごとに Performance mark を分離する連番。 */
let operationSequence = 0

/**
 * Performance API が利用可能なら現在時刻を返す。
 * @returns 高精度時刻、未対応環境では epoch milliseconds を返す。
 */
const getPerformanceNow = (): number =>
  typeof performance === 'undefined' ? Date.now() : performance.now()

/**
 * Performance mark と measure を best effort で記録する。
 * @param params phase、operation ID、計測対象 callback を受け取る。
 * @returns callback の結果をそのまま返す。
 */
const measureWithPerformanceApi = async <Result>({
  phase,
  operationId,
  task,
}: {
  readonly phase: DbSyncTelemetryPhase
  readonly operationId: number
  readonly task: () => Promise<Result>
}): Promise<Result> => {
  const startMark = `${phase}:${operationId}:start`
  const endMark = `${phase}:${operationId}:end`
  let marked = false
  try {
    if (typeof performance !== 'undefined') {
      performance.mark(startMark)
      marked = true
    }
  } catch {
    marked = false
  }

  try {
    return await task()
  } finally {
    if (marked) {
      try {
        performance.mark(endMark)
        performance.measure(phase, startMark, endMark)
        performance.clearMarks(startMark)
        performance.clearMarks(endMark)
      } catch {
        // Telemetry failure must not affect DB backup behavior.
      }
    }
  }
}

/**
 * Sentry client が初期化済みの場合だけ v8 tracing span を開始する。
 * @param params span 名、属性、処理 callback を受け取る。
 * @returns callback の結果をそのまま返す。
 */
const runWithSentrySpan = async <Result>({
  phase,
  attributes,
  task,
}: {
  readonly phase: DbSyncTelemetryPhase
  readonly attributes?: DbSyncSpanAttributes
  readonly task: (params: {
    readonly span: Span | undefined
  }) => Promise<Result>
}): Promise<Result> => {
  if (getClient() === undefined) {
    return task({ span: undefined })
  }
  return startSpan(
    {
      name: phase,
      op: 'db.backup',
      attributes,
    },
    (span) => task({ span }),
  )
}

/**
 * 対応 browser で operation 中だけ long task を集計する。
 * @returns observer を停止して count と最大 duration を返す callback を返す。
 */
export const startDbSyncLongTaskObserver = (): StopLongTaskObserver => {
  let count = 0
  let longestDurationMs = 0
  let observer: PerformanceObserver | undefined

  const recordEntries = ({
    entries,
  }: {
    readonly entries: readonly PerformanceEntry[]
  }): void => {
    for (const entry of entries) {
      count += 1
      longestDurationMs = Math.max(longestDurationMs, entry.duration)
    }
  }

  try {
    if (
      typeof PerformanceObserver !== 'undefined' &&
      PerformanceObserver.supportedEntryTypes.includes('longtask')
    ) {
      observer = new PerformanceObserver((entryList) => {
        recordEntries({ entries: entryList.getEntries() })
      })
      observer.observe({ entryTypes: ['longtask'] })
    }
  } catch {
    observer = undefined
  }

  return () => {
    if (observer !== undefined) {
      recordEntries({ entries: observer.takeRecords() })
    }
    observer?.disconnect()
    return { count, longestDurationMs }
  }
}

/**
 * recorder が渡された場合だけ phase を計測し、通常 caller では処理だけを実行する。
 * @param params optional recorder、phase、処理 callback を受け取る。
 * @returns 処理 callback の結果を返す。
 */
export const measureDbSyncTelemetryPhase = async <Result>({
  telemetry,
  phase,
  task,
}: {
  readonly telemetry: DbSyncTelemetry | undefined
  readonly phase: Exclude<DbSyncTelemetryPhase, 'db-backup.total'>
  readonly task: () => Promise<Result>
}): Promise<Result> => {
  if (telemetry === undefined) {
    return task()
  }
  return telemetry.measurePhase({ phase, task })
}

/**
 * 1 回の DB backup を total span と Performance measure で囲む。
 * @param params 非機密 metadata と telemetry recorder を使う backup callback を受け取る。
 * @returns backup callback の結果を返す。
 */
export const runDbSyncTelemetryOperation = async <Result>({
  metadata,
  task,
}: {
  readonly metadata: DbSyncTelemetryMetadata
  readonly task: (params: {
    readonly telemetry: DbSyncTelemetry
  }) => Promise<Result>
}): Promise<Result> => {
  operationSequence += 1
  const operationId = operationSequence
  const baseAttributes: DbSyncSpanAttributes = {
    'db_backup.trigger_reason': metadata.triggerReason,
    'db_backup.dirty_age_ms': metadata.dirtyAgeMs,
    'db_backup.last_write_age_ms': metadata.lastWriteAgeMs,
    'db_backup.user_idle_age_ms': metadata.userIdleAgeMs,
    'db_backup.visibility_state': metadata.visibilityState,
  }

  return measureWithPerformanceApi({
    phase: 'db-backup.total',
    operationId,
    task: () =>
      runWithSentrySpan({
        phase: 'db-backup.total',
        attributes: baseAttributes,
        task: async ({ span: totalSpan }) => {
          const startedAt = getPerformanceNow()
          const stopLongTaskObserver = startDbSyncLongTaskObserver()
          const setTotalAttribute = ({
            key,
            value,
          }: {
            readonly key: string
            readonly value: number
          }): void => {
            totalSpan?.setAttribute(key, value)
          }
          const telemetry: DbSyncTelemetry = {
            measurePhase: ({ phase, task: phaseTask }) =>
              measureWithPerformanceApi({
                phase,
                operationId,
                task: () =>
                  runWithSentrySpan({
                    phase,
                    task: async () => phaseTask(),
                  }),
              }),
            setRawDbSizeBytes: ({ bytes }) => {
              setTotalAttribute({
                key: 'db_backup.raw_size_bytes',
                value: bytes,
              })
            },
            setGzipSizeBytes: ({ bytes }) => {
              setTotalAttribute({
                key: 'db_backup.gzip_size_bytes',
                value: bytes,
              })
            },
          }

          try {
            return await task({ telemetry })
          } finally {
            const longTasks = stopLongTaskObserver()
            totalSpan?.setAttributes({
              'db_backup.total_duration_ms': Math.max(
                0,
                getPerformanceNow() - startedAt,
              ),
              'db_backup.long_task_count': longTasks.count,
              'db_backup.longest_long_task_duration_ms':
                longTasks.longestDurationMs,
            })
          }
        },
      }),
  })
}
